import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import type { DialCode } from "@/types/dialcode";

export type PhoneFieldProps = {
  code: string;
  phone: string;
  dialCodes: DialCode[];
  onCodeChange: (value: string) => void;
  onPhoneChange: (value: string) => void;
  error?: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  onPhoneBlur?: () => void;
};

const FIELD_CLASS =
  "h-14 w-full rounded-2xl border border-input bg-card px-4 text-sm font-medium text-muted-foreground placeholder:text-border";

export default function PhoneField({
  code,
  phone,
  dialCodes,
  onCodeChange,
  onPhoneChange,
  error,
  label = "Contact No",
  placeholder = "Enter phone number",
  disabled = false,
  onPhoneBlur,
}: PhoneFieldProps) {
  const [open, setOpen] = useState(false);
  const isCodePlaceholder = !code || code === "Country Code";
  const hasPhoneValue = typeof phone === "string" && phone.trim().length > 0;
  const countryCodeError =
    error ??
    (hasPhoneValue && isCodePlaceholder
      ? "Please select a country code."
      : undefined);

  return (
    <div className="flex flex-col gap-2">
      <Label className="text-xs font-bold tracking-wide text-field">
        {label} <span className="text-destructive">*</span>
      </Label>

      <div className="grid grid-cols-[110px_1fr] gap-2">
        {/* Country Code */}
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger
            type="button"
            disabled={disabled || dialCodes.length === 0}
            className={cn(
              FIELD_CLASS,
              "flex items-center justify-between gap-1",
              "focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50",
              countryCodeError && "border-destructive text-destructive ring-3 ring-destructive/20",
              disabled && "cursor-not-allowed opacity-50",
            )}
          >
            <span className={cn(isCodePlaceholder && "text-xs text-muted")}>
              {code || "+60"}
            </span>

            <ChevronDown size={16} className="shrink-0 text-muted-foreground" />
          </PopoverTrigger>

          <PopoverContent
            className="w-64 p-0"
            align="start"
            side="bottom"
            sideOffset={4}
            positionMethod="fixed"
            collisionAvoidance={{ side: "none" }}
          >
            <Command>
              <CommandInput placeholder="Search country..." />

              <CommandList className="max-h-72">
                <CommandEmpty>No country found.</CommandEmpty>

                <CommandGroup>
                  {dialCodes.map((dialCode) => (
                    <CommandItem
                      key={dialCode.code}
                      value={`${dialCode.name} ${dialCode.dialCode}`}
                      onSelect={() => {
                        onCodeChange(dialCode.dialCode);
                        setOpen(false);
                      }}
                      className={cn(
                        "flex items-center gap-2 text-sm",
                        code === dialCode.dialCode &&
                          "bg-sky-50 data-[selected=true]:bg-sky-100",
                      )}
                    >
                      <span className="w-12 shrink-0 font-medium">
                        {dialCode.dialCode}
                      </span>

                      <span className="text-muted-foreground">
                        {dialCode.name}
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {/* Phone Number */}
        <input
          type="text"
          value={phone}
          onChange={(e) => {
            const value = e.target.value.replace(/\D/g, "").slice(0, 15);

            onPhoneChange(value);
          }}
          onBlur={onPhoneBlur}
          placeholder={placeholder}
          maxLength={15}
          disabled={disabled}
          className={cn(
            FIELD_CLASS,
            "outline-none transition-colors",
            "focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50",
            countryCodeError && "border-destructive ring-3 ring-destructive/20",
            disabled && "cursor-not-allowed opacity-50",
          )}
        />
      </div>

      {countryCodeError && <p className="text-xs text-destructive">{countryCodeError}</p>}
    </div>
  );
}